import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { concatMap, exhaustMap, pipe, tap } from 'rxjs';

import { Federation } from '@federations/models/federation';
import { FederationsService } from '@federations/services/federations.service';
import { ToastNotificationsService } from '@shared/services/toast-notifications.service';

interface FederationState {
  data: Federation[];
  loading: boolean;
  loaded: boolean;
  saving: boolean;
  error: HttpErrorResponse | null;
}

const INITIAL_STATE: FederationState = {
  data: [],
  loading: false,
  loaded: false,
  saving: false,
  error: null
};

export const FEDERATION_STORE = signalStore(
  withState<FederationState>(INITIAL_STATE),

  withMethods(
    (
      store,
      federationsService = inject(FederationsService),
      toastNotificationsService = inject(ToastNotificationsService)
    ) => ({
      list: rxMethod<void>(
        pipe(
          tap(() =>
            patchState(store, {
              loading: true,
              loaded: false,
              error: null
            })
          ),

          exhaustMap(() =>
            federationsService.list().pipe(
              tapResponse({
                next: (result: Federation[]) => {
                  patchState(store, {
                    data: result,
                    loading: false,
                    loaded: true
                  });
                },

                error: (error: HttpErrorResponse) => {
                  toastNotificationsService.error('Loading federations failed');

                  patchState(store, {
                    loading: false,
                    loaded: true,
                    error
                  });
                }
              })
            )
          )
        )
      ),

      create: rxMethod<Federation>(
        pipe(
          tap(() => patchState(store, { saving: true, error: null })),

          exhaustMap(federation =>
            federationsService.create(federation).pipe(
              tapResponse({
                next: (result: Federation) => {
                  toastNotificationsService.success('Federation created');

                  patchState(store, {
                    data: [...store.data(), result],
                    saving: false
                  });
                },
                error: (error: HttpErrorResponse) => {
                  toastNotificationsService.error('Create federation failed');
                  patchState(store, { error, saving: false });
                }
              })
            )
          )
        )
      ),

      update: rxMethod<Federation>(
        pipe(
          tap(() => patchState(store, { saving: true, error: null })),

          concatMap(federation =>
            federationsService.update(federation).pipe(
              tapResponse({
                next: (result: Federation) => {
                  toastNotificationsService.success('Federation updated');

                  patchState(store, {
                    data: store
                      .data()
                      .map(x => (x.id === result.id ? { ...x, ...result } : x)),
                    saving: false
                  });
                },
                error: (error: HttpErrorResponse) => {
                  toastNotificationsService.error('Update federation failed');

                  patchState(store, { error, saving: false });
                }
              })
            )
          )
        )
      ),

      clear(): void {
        patchState(store, { ...INITIAL_STATE });
      }
    })
  )
);
